import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { Observable } from 'rxjs';

import { toQueryParams } from 'src/app/api/api-utils';
import {
  USE_CASE_DELETE,
  USE_CASE_GET,
  USE_CASE_GET_TEMPLATES,
  USE_CASE_GET_TEMPLATE_SCHEMA,
  USE_CASE_POST_PREVIEW,
  USE_CASE_POST_PROVISION,
} from 'src/app/api/endpoints/use-case.endpoints';
import {
  UseCaseInstance,
  UseCasePreviewResponse,
  UseCaseProvisionRequest,
  UseCaseProvisionResponse,
  UseCaseTemplateSchema,
  UseCaseTemplatesResponse,
  UseCaseWorkspaceChoice,
} from 'src/app/core/models/use-case.models';

/**
 * API service for the Create Use Case wizard (backend SPEC §9). The
 * SensikaHttpInterceptor unwraps the outer {body} envelope, so each method
 * sees the inner §9 payload directly. The two GET reads are consumed through
 * query hooks (use-case.queries.ts); preview/provision are direct calls
 * (single server-side orchestration, §1.4 - never chained client-side).
 */
@Injectable({ providedIn: 'root' })
export class UseCaseApiService {
  private readonly http = inject(HttpClient);

  /** Templates visible to the acting user's company (§9). */
  getTemplates(): Observable<UseCaseTemplatesResponse> {
    return this.http.get<UseCaseTemplatesResponse>(USE_CASE_GET_TEMPLATES);
  }

  /** Full input schema for the wizard's dynamic form (§9). */
  getTemplateSchema(templateId: string): Observable<UseCaseTemplateSchema> {
    const params = toQueryParams({ template_id: templateId });
    return this.http.get<UseCaseTemplateSchema>(USE_CASE_GET_TEMPLATE_SCHEMA, {
      params,
    });
  }

  /**
   * Dry-run plan + validation + quota (§9.2). No writes. Carries the nested
   * workspace choice (§9.5) so preview validates - and resolves the new
   * workspace's default title from - the same object identity provision will
   * create.
   *
   * @param options the template id, business/preferences inputs, and §9.5 workspace choice
   * @returns the preview response
   */
  preview(options: {
    templateId: string;
    inputs: Record<string, unknown>;
    workspace: UseCaseWorkspaceChoice;
  }): Observable<UseCasePreviewResponse> {
    return this.http.post<UseCasePreviewResponse>(USE_CASE_POST_PREVIEW, {
      template_id: options.templateId,
      inputs: options.inputs,
      workspace: options.workspace,
    });
  }

  /**
   * Provision the full object graph in one server-side call (§8, §9). Returns
   * the success envelope on 200, or the valid:false envelope when pre-write
   * validation fails; a §9.4 failure surfaces as an HTTP 422 error.
   */
  provision(
    request: UseCaseProvisionRequest,
  ): Observable<UseCaseProvisionResponse> {
    return this.http.post<UseCaseProvisionResponse>(
      USE_CASE_POST_PROVISION,
      request,
    );
  }

  /** A provisioned instance: status, input snapshot, manifest (§9). */
  get(objectId: string): Observable<UseCaseInstance> {
    return this.http.get<UseCaseInstance>(USE_CASE_GET(objectId));
  }

  /** Teardown a use case via the backend handlers' rollback (§9). */
  delete(objectId: string): Observable<unknown> {
    return this.http.delete(USE_CASE_DELETE(objectId));
  }
}
